// GET /api/v1/dashboard and its siblings, and what in them needs the
// customer's attention. Shared by the dashboard page and the navbar's
// notifications, so both list the same things in the same order.
//
// Every figure is the caller's own account (service.UserDashboard).

export interface DashSummary {
  /** Every site in exactly one bucket: they add up to total. */
  sites: { total: number; active: number; draft: number; disabled: number; suspended: number }
  /** expiring and expired count only certificates still being served. */
  certs: { total: number; expiring: number; expired: number }
  /** active includes expiring: both are still in force. */
  subscriptions: { active: number; expiring: number; expired: number; cancelled: number }
  /** This period's use of every package in force, added up. A limit of 0
   *  is no ceiling. Null where packages are not metered. */
  usage: {
    bytes_used: number
    bytes_limit: number
    requests_used: number
    requests_limit: number
    subscriptions: number
  } | null
  /** Added today, in the customer's own day. */
  today: { sites: number; hostnames: number; subscriptions: number }
  counts: {
    /** Every name the sites answer on: their own and their aliases'. */
    hostnames: number
    deny_ips: number
    allow_ips: number
    credentials: number
    /** Today's purges and prefetches. */
    cache_jobs: number
  }
  /** 工单 with a reply from support not read yet. */
  tickets?: { unread: number }
  /** 自动续费: packages in force that could renew themselves and do not;
   *  packages due whose last automatic renewal failed, and the first one's
   *  reason (a server error code). */
  auto_renew?: { off: number; failed: number; reason?: string }
}

/** One stretch of 监控趋势 (service.UserMonitorPoint). */
export interface MonitorPoint {
  t: string
  /** Bytes per second, the stretch's busiest minute. */
  peak_bps: number
  requests: number
  bytes: number
  hits: number
  s4xx: number
  s5xx: number
  /** Requests turned away: blocked or challenged. */
  blocked: number
  blocked_ips: number
}
export type MonitorTotals = Omit<MonitorPoint, 't'>
export interface Monitor {
  step: number
  /** Whether the access logs (ClickHouse) exist at all. */
  logs: boolean
  points: MonitorPoint[]
  totals: MonitorTotals
  previous: MonitorTotals
}

export type SiteState = 'active' | 'draft' | 'suspended' | 'disabled'
/** One tile of 站点概况: a site and its traffic today. */
export interface DashSite {
  id: number
  name: string
  state: SiteState
  /** Why a suspended site is suspended. */
  reason?: string
  aliases: number
  requests: number
  bytes: number
  hits: number
}
export interface DashSites {
  logs: boolean
  items: DashSite[]
}

/** The share of a ceiling used, or null when there is no ceiling. */
export function share(used: number, limit: number): number | null {
  return limit > 0 ? used / limit : null
}

/** A share as "45%": whole from 10%, one decimal below. */
export function pct(r: number): string {
  const p = r * 100
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`
}

export interface Attention {
  /** How many, or how much: the message's {n}. */
  n: number | string
  /** dashboard.tag_<tag>: what kind of thing it is about. */
  tag: 'cert' | 'site' | 'sub' | 'quota' | 'ticket'
  color: string
  /** dashboard.<key>: the message. */
  key: string
  /** The page that deals with it. */
  route: string
}

// From 80% of a period's ceiling a package is worth a look: at 100% its
// sites stop being served until the period turns.
const QUOTA_WARN = 0.8

// Worst first. Only what is not zero is listed; an empty list says all is
// well.
export function attentionItems(s: DashSummary | null | undefined): Attention[] {
  if (!s) return []
  const rows: Attention[] = [
    { n: s.sites.suspended, tag: 'site', color: 'red', key: 'sitesSuspended', route: 'domains' },
    { n: s.certs.expired, tag: 'cert', color: 'red', key: 'certsExpired', route: 'certificates' },
    { n: s.auto_renew?.failed ?? 0, tag: 'sub', color: 'red', key: 'autoRenewFailed', route: 'my-packages' },
    { n: s.certs.expiring, tag: 'cert', color: 'orangered', key: 'certsExpiring', route: 'certificates' },
    { n: s.subscriptions.expiring, tag: 'sub', color: 'orange', key: 'subsExpiring', route: 'my-packages' },
    { n: s.tickets?.unread ?? 0, tag: 'ticket', color: 'arcoblue', key: 'ticketsReplied', route: 'tickets' },
    { n: s.sites.draft, tag: 'site', color: 'cyan', key: 'sitesDraft', route: 'domains' },
  ]
  const u = s.usage
  if (u) {
    const bytes = share(u.bytes_used, u.bytes_limit)
    const requests = share(u.requests_used, u.requests_limit)
    if (bytes !== null && bytes >= QUOTA_WARN) {
      rows.splice(1, 0, { n: pct(bytes), tag: 'quota', color: bytes >= 1 ? 'red' : 'orange', key: 'trafficHigh', route: 'my-packages' })
    }
    if (requests !== null && requests >= QUOTA_WARN) {
      rows.splice(1, 0, { n: pct(requests), tag: 'quota', color: requests >= 1 ? 'red' : 'orange', key: 'requestsHigh', route: 'my-packages' })
    }
  }
  return rows.filter((r) => typeof r.n === 'string' || r.n > 0)
}
